import { Component } from '@angular/core';
import { FormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { SharedMethodsService } from 'src/app/services/shared-methods.service';
import { CommonDataService } from 'src/app/services/storage/common-data.service';
import { UserStorageService } from 'src/app/services/storage/user-storage.service';
import { SpeciesAtRiskReport } from '../../data-models/report-models/species-at-risk-report.model';
import { Trip } from '../../data-models/trip-models/trip.model';
import { UserData } from '../../data-models/user-models/user-data.model';
import { AuthenticationService } from '../../services/authentication.service';
import { MatDialog } from '@angular/material/dialog';
import { ConfirmationDialogComponent } from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import { Coordinate } from 'src/app/data-models/coordinate.model';
import { coordValidator, latitudeValidator, longitudeValidator } from 'src/app/data-models/validator.model';
import { GeolocationMethodsService } from 'src/app/services/geolocation-methods.service';


@Component({
    selector: 'app-species-at-risk-report',
    templateUrl: './species-at-risk-report.component.html',
    styleUrls: ['./species-at-risk-report.component.css'],
    standalone: false
})


export class SpeciesAtRiskReportComponent {

  isReportSending: boolean = false; // flag that tells us if the report is sending (so we can show the loading screen)

  // data preambles
  currTrip: Trip = new Trip();
  userDetails: UserData = new UserData();
  tripReportIndex!: number; //Index of report in the current trip
  speciesAtRiskReport!: SpeciesAtRiskReport;
  conditionOptions: string[] = ['Alive', 'Dead', 'Most alive', 'Most dead'];  //hardcoded values to be used in the form
  speciesOptions: string[] = ['Bass, Striped', 'Beluga', 'Wolffish, Northern', 'Wolffish, Spotted', 'Turtle, Loggerhead Sea', 'Turtle, Leatherback Sea', 'Shark, White', 'Whale, Blue', 'Whale, North Atlantic Right'];  //hardcoded values to be used in the form


  // validation check for summary page
  encounterTimeValid: boolean = false;

  isComplete: boolean = false;

  // report data
  speciesAtRiskForm: UntypedFormGroup = new UntypedFormGroup({
    latitude: new FormControl<Coordinate>(new Coordinate, [Validators.required, coordValidator(), latitudeValidator(40, 70)]),
    longitude: new FormControl<Coordinate>(new Coordinate, [Validators.required, coordValidator(), longitudeValidator(114, 165)]),  
    species: new FormControl<string>('', [Validators.required]),
    numSpecimens: new FormControl<number>(null!, [Validators.required, Validators.max(9999)]),    
    weight: new FormControl<number>(null!, Validators.max(999999.999)),                               
    condition: new FormControl<string>('', [Validators.required]),
    comments: new FormControl<string>('')                          
  });

  encounterDate: Date = new Date();
  encounterTime: { hour: number, minute: number } = { hour: 0, minute: 0 };
  maxDate: Date = new Date();

  formUpdateSubscription!: Subscription;

  // page to page
  numPages: number = 0;
  currPage: number = 0;
  spinnerProgress: number = 0;
  spinnerIncrement: number = 0;

  constructor(
    public geolocationMethod: GeolocationMethodsService,
    private route: ActivatedRoute,
    private authenticationService: AuthenticationService,
    private dialog: MatDialog,
    public commonDataService: CommonDataService,
    public sharedMethodsService: SharedMethodsService
  ) {

  }




  /*  ---------- Report Initialization Methods  ---------- */


  ngOnInit(): void {

    // array of documents needed from the common data pouch for this component
    let requiredData: string[] = ['license_areas_list', 'management_areas_list'];

    this.authenticationService.getUserDetails().then(() => {
      this.userDetails = UserStorageService.currUser;
    }).then(() => {
      UserStorageService.getCurrTrip().then(() => {
        if (this.authenticationService.dataError(UserStorageService.error)) {
          return;
        }
        this.currTrip = UserStorageService.currTrip;
        let reportId = <string>this.route.snapshot.paramMap.get('id');
        this.tripReportIndex = this.currTrip.speciesAtRiskReports.findIndex((report: SpeciesAtRiskReport) => report.internalId == reportId);
        this.speciesAtRiskReport = this.currTrip.speciesAtRiskReports[this.tripReportIndex];
        this.commonDataService.getSpecifiedDocs(this.userDetails.userEmail, requiredData).then(() => {
          this.initialize();
        });
      });
    });

  }


  // initialize the data
  initialize(): void {


    this.isComplete = this.route.snapshot.queryParams.status === 'complete';

    
    if (this.route.snapshot.queryParams.status == 'new') {

      // set the encounter time to the current time
      this.encounterTime = {
        hour: this.encounterDate.getHours(),
        minute: this.encounterDate.getMinutes()
      };

    } else {
      this.patchForm()
    }

    this.subscribeToFormChanges();
    

    //If the form is complete (uneditable) set it to view only summary screen
    if (this.route.snapshot.queryParams.status == 'complete') {
      this.numPages = -1;
      this.currPage = -1;
    } else {
      this.numPages = 4;
      this.spinnerIncrement = 100 / this.numPages;      
    }
    this.toNextPage();
    

  }


  //Populates the form controls with values from the species at risk report
  patchForm() {
    this.speciesAtRiskForm.patchValue({
      latitude: this.speciesAtRiskReport.latitude,
      longitude: this.speciesAtRiskReport.longitude,
      species: this.speciesAtRiskReport.species,
      numSpecimens: this.speciesAtRiskReport.numSpecimens,
      weight: this.speciesAtRiskReport.weight,
      condition: this.speciesAtRiskReport.condition,
      comments: this.speciesAtRiskReport.comments
    });
    this.encounterDate = this.speciesAtRiskReport.encounterDate;
    this.encounterTime = { hour: this.speciesAtRiskReport.encounterDate.getHours(), minute: this.speciesAtRiskReport.encounterDate.getMinutes() };

  }


  // Subscribe to all changes in the form to update and save as changes occur
  private subscribeToFormChanges(): void {

    this.formUpdateSubscription = this.speciesAtRiskForm.valueChanges.subscribe(form => {
      const speciesReport = this.currTrip.speciesAtRiskReports[this.tripReportIndex];
      speciesReport.latitude = form.latitude;
      speciesReport.longitude = form.longitude;
      speciesReport.species = form.species;
      speciesReport.numSpecimens = form.numSpecimens;
      speciesReport.weight = form.weight;
      speciesReport.condition = form.condition;
      speciesReport.comments = form.comments;

      UserStorageService.saveTrip(this.currTrip, true, false);
    });
  }

  dateTimeChange() {
    this.currTrip.speciesAtRiskReports[this.tripReportIndex].encounterDate = this.encounterDate;
    this.currTrip.speciesAtRiskReports[this.tripReportIndex].encounterDate.setMinutes(this.encounterTime.minute);
    this.currTrip.speciesAtRiskReports[this.tripReportIndex].encounterDate.setHours(this.encounterTime.hour);
    UserStorageService.saveTrip(this.currTrip, true, false);
  }



  /* ---------- Page Navigation Methods ---------- */


  // check current page before going to the next one
  toNextPage(): void {

    if (this.currPage <= this.numPages) {
      if (this.currPage === 4 || this.currPage === -1) { // if currPage is -1 then this report is complete and view only
        this.setSummaryPage();
      }
      this.currPage++;
      this.spinnerProgress += this.spinnerIncrement;
    }
    else {
      this.saveReport();
    }

  }


  // back one page
  toPreviousPage(): void {

    if (this.currPage <= 1) {

      this.sharedMethodsService.toHome(this.userDetails.currHomepage);

    }

    this.currPage--;
    this.spinnerProgress -= this.spinnerIncrement;

  }

  // set all of the data to display on the summary based on the entered report fields
  setSummaryPage(): void {

    // set validation values
    this.encounterTimeValid = !!this.encounterTime;

  }



  /* ---------- Report Completion Methods ---------- */


  isReportValid(): boolean {

    return this.speciesAtRiskForm.valid &&
      this.encounterTimeValid;

  }


  // send start fishing report to DFO
  saveReport(): void {

    if (this.isReportValid()) {
      this.isReportSending = true;
      this.saveReportData(); // save report data
      this.saveToPouchDB();
      this.speciesAtRiskReport.isSubmitted = true;
      // transmission methods will go here
    }
    else {
      this.sharedMethodsService.toastInvalidForm();
    }

  }

  // saves report data using the form data collected
  saveReportData(): void {

    // save relevant information gathered in the form
    this.speciesAtRiskReport.encounterDate = this.encounterDate;
    this.speciesAtRiskReport.latitude = this.speciesAtRiskForm.get('latitude')?.value;
    this.speciesAtRiskReport.longitude = this.speciesAtRiskForm.get('longitude')?.value;
    this.speciesAtRiskReport.species = this.speciesAtRiskForm.get('species')?.value;
    this.speciesAtRiskReport.numSpecimens = this.speciesAtRiskForm.get('numSpecimens')?.value;
    this.speciesAtRiskReport.weight = this.speciesAtRiskForm.get('weight')?.value;
    this.speciesAtRiskReport.condition = this.speciesAtRiskForm.get('condition')?.value;
    this.speciesAtRiskReport.comments = this.speciesAtRiskForm.get('comments')?.value.trim();
    this.speciesAtRiskReport.reportId = SharedMethodsService.generateReportID("");

  }

  // updates the trip document in the user's pouch db with the saved pause report then navigates to the report submission page
  saveToPouchDB(): void {

    // save curr trip to the user's storage
    UserStorageService.saveTrip(this.currTrip).then(() => {
      // navigate to the home page
      this.sharedMethodsService.toHome(this.userDetails.currHomepage);
      this.sharedMethodsService.toastSavedSuccessfully();
    });

  }


  deleteReport() {
    this.dialog.open(ConfirmationDialogComponent, {
      data: { screen: 'delete_report' }
    }).afterClosed().subscribe(result => {
      if (result) {
        UserStorageService.deleteTripReport((trip) => trip.speciesAtRiskReports, this.speciesAtRiskReport.internalId)
          .then(() => this.sharedMethodsService.toHome(this.userDetails.currHomepage));
      }
    });
  }

  ngOnDestroy() {
    if (this.formUpdateSubscription) this.formUpdateSubscription.unsubscribe();
  }

}

